import { createFileRoute, redirect } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ColumnDef } from '@tanstack/react-table';
import { z } from 'zod';
import { toast } from 'sonner';
import {
  PromoService,
  type PromoCode,
  type PromoPartnerSale,
} from '@/api/promo-service';
import { PageHeader } from '@/components/page-header';
import { PageContent } from '@/components/page-content';
import { SectionHeader } from '@/components/section-header';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { DataTable } from '@/components/ui/data-table';
import { RowActionsMenu } from '@/components/ui/row-actions-menu';
import { useAppForm } from '@/hooks/use-app-form';

export const Route = createFileRoute('/_auth/_side-layout/super-admin/promos')({
  component: SuperAdminPromosPage,
  beforeLoad: async ({ context }) => {
    if (context.platformRole !== 'admin') throw redirect({ to: '/' });
  },
});

const codeSchema = z.object({
  code: z
    .string()
    .regex(/^[A-Za-z0-9_-]{1,200}$/, 'Letters, digits, dashes or underscores only'),
  partnerName: z.string().optional(),
});

const dollars = (cents: number) => `$${(cents / 100).toLocaleString('en-US')}`;

function SuperAdminPromosPage() {
  const qc = useQueryClient();
  const codes = useQuery({ queryKey: ['promo-codes'], queryFn: PromoService.listCodes });
  const sales = useQuery({ queryKey: ['promo-sales'], queryFn: PromoService.getSales });

  const save = useMutation({
    mutationFn: PromoService.createCode,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['promo-codes'] });
      toast.success('Promo code saved');
    },
    onError: (e: Error) => toast.error(e.message || 'Could not save the code'),
  });

  const form = useAppForm({
    defaultValues: { code: '', partnerName: '' },
    validators: { onSubmit: codeSchema },
    onSubmit: async ({ value }) => {
      await save.mutateAsync({
        code: value.code,
        partnerName: value.partnerName ?? null,
      });
      form.reset({ code: '', partnerName: '' });
    },
  });

  const codeColumns: ColumnDef<PromoCode>[] = [
    { accessorKey: 'code', header: 'Code' },
    { accessorKey: 'campaign', header: 'Promo' },
    {
      accessorKey: 'partnerName',
      header: 'Partner',
      cell: ({ row }) => row.original.partnerName ?? 'Direct',
    },
    {
      accessorKey: 'active',
      header: 'Status',
      cell: ({ row }) => (row.original.active ? 'Active' : 'Off'),
    },
    {
      id: 'actions',
      cell: ({ row }) => (
        <RowActionsMenu
          actions={[
            {
              label: row.original.active ? 'Deactivate' : 'Activate',
              onSelect: () =>
                save.mutate({
                  code: row.original.code,
                  campaign: row.original.campaign,
                  partnerName: row.original.partnerName,
                  active: !row.original.active,
                }),
            },
          ]}
        />
      ),
    },
  ];

  const salesColumns: ColumnDef<PromoPartnerSale>[] = [
    {
      accessorKey: 'partnerCode',
      header: 'Partner Code',
      cell: ({ row }) => row.original.partnerCode ?? 'Direct / none',
    },
    { accessorKey: 'sales', header: 'Sales' },
    {
      accessorKey: 'grossCents',
      header: 'Gross',
      cell: ({ row }) => dollars(row.original.grossCents),
    },
  ];

  return (
    <PageContent>
      <PageHeader
        title="Promotions"
        description="Manage promo codes and track partner sales across promo campaigns."
      />
      <Card>
        <CardContent className="space-y-4">
          <SectionHeader
            title="Promo Codes"
            subTitle="Codes unlock the Lifetime Deal page. A partner name attributes that code's sales for affiliate payouts."
          />
          <form
            onSubmit={(e) => {
              e.preventDefault();
              form.handleSubmit();
            }}
          >
            <form.AppForm>
              <div className="grid items-end gap-4 sm:grid-cols-[1fr_1fr_auto]">
                <form.AppField name="code">
                  {(field) => <field.Input label="Code" placeholder="LAUNCH or PARTNER-X" />}
                </form.AppField>
                <form.AppField name="partnerName">
                  {(field) => (
                    <field.Input label="Partner (Optional)" placeholder="Acme Newsletter" />
                  )}
                </form.AppField>
                <Button type="submit" loading={save.isPending}>
                  Add Code
                </Button>
              </div>
            </form.AppForm>
          </form>
          <DataTable
            columns={codeColumns}
            data={codes.data ?? []}
            isLoading={codes.isLoading}
            emptyMessage="No promo codes yet"
          />
        </CardContent>
      </Card>
      <Card>
        <CardContent className="space-y-4">
          <SectionHeader
            title="Partner Sales"
            subTitle="Redeemed and pending purchases grouped by the code used at checkout."
          />
          <DataTable
            columns={salesColumns}
            data={sales.data ?? []}
            isLoading={sales.isLoading}
            emptyMessage="No sales yet"
          />
        </CardContent>
      </Card>
    </PageContent>
  );
}
